import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import { resolveDemDisplayUser } from './demUserNames';
import { demMainCustomerCode, fetchDemCustomer } from './demCustomers';
import { fetchPricesForItems, getNavisionPrice } from './demPoPricing';
import type { DemCustomer } from '../types/dem';

/** Match B2B Ordering Portal `navRound` (2 d.p.). */
function navRound(num: number): number {
  return Number(Math.round(Number(`${num}e2`)) + 'e-2');
}

export type DemOrderBatch = {
  id: string;
  order_id: string;
  batch_no: number;
  reason: string | null;
  created_by: string;
  created_at: string;
  exported_by: string | null;
  exported_at: string | null;
};

export type DemOrderBatchItem = {
  item_code: string;
  description: string;
  qty: number;
  uom: string;
  batch_id: string | null;
};

export type DemOrderBatchLineInput = {
  item_code: string;
  description: string;
  qty: number;
  uom: string;
};

export type ExportRow = Record<string, string | number>;

export function batchIsPendingExport(batch: Pick<DemOrderBatch, 'exported_at'>): boolean {
  return !batch.exported_at;
}

export function orderHasPendingExportBatches(
  batches: Pick<DemOrderBatch, 'exported_at'>[] | null | undefined,
): boolean {
  if (!batches?.length) return true;
  return batches.some((b) => batchIsPendingExport(b));
}

/** List Creator: last amend creator while anything pending; all names when fully exported. */
export function formatOrderCreatorDisplay(
  batches: DemOrderBatch[] | null | undefined,
  userNames: Map<string, string>,
  fallbackCreatedBy = '',
): string {
  const sorted = [...(batches ?? [])].sort((a, b) => a.batch_no - b.batch_no);
  if (sorted.length === 0) {
    return resolveDemDisplayUser(fallbackCreatedBy, userNames) || '—';
  }

  if (orderHasPendingExportBatches(sorted)) {
    const last = sorted[sorted.length - 1]!;
    const raw = last.created_by.trim() || fallbackCreatedBy;
    return resolveDemDisplayUser(raw, userNames) || '—';
  }

  const names: string[] = [];
  const seen = new Set<string>();
  for (const batch of sorted) {
    const raw = batch.created_by.trim() || (batch.batch_no === 0 ? fallbackCreatedBy : '');
    const label = resolveDemDisplayUser(raw, userNames).trim();
    if (!label || label === '—') continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(label);
  }
  return names.length ? names.join('/') : '—';
}

/** List Exporter: blank while any batch pending; joined names when all exported. */
export function formatOrderExporterDisplay(
  batches: DemOrderBatch[] | null | undefined,
  userNames: Map<string, string>,
  fallbackExportedBy = '',
): string {
  const sorted = [...(batches ?? [])].sort((a, b) => a.batch_no - b.batch_no);
  if (sorted.length === 0) {
    return resolveDemDisplayUser(fallbackExportedBy, userNames);
  }
  if (orderHasPendingExportBatches(sorted)) return '';

  const names: string[] = [];
  const seen = new Set<string>();
  for (const batch of sorted) {
    const raw = (batch.exported_by ?? '').trim();
    if (!raw) continue;
    const label = resolveDemDisplayUser(raw, userNames).trim();
    if (!label || label === '—') continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(label);
  }
  return names.join('/');
}

export function batchDisplayStatus(
  batch: DemOrderBatch,
  orderStatus: string,
): 'Pending' | 'Uploading' | 'Processed' | 'Cancelled' {
  if (orderStatus === 'Cancelled') return 'Cancelled';
  if (!batch.exported_at) return 'Pending';
  if (orderStatus === 'Uploading') return 'Uploading';
  return 'Processed';
}

export function canAmendOrder(status: string): boolean {
  return status === 'Pending' || status === 'Processed';
}

export function parseOrderBatches(raw: unknown): DemOrderBatch[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      const r = row as Record<string, unknown>;
      return {
        id: String(r.id ?? '').trim(),
        order_id: String(r.order_id ?? '').trim(),
        batch_no: Number(r.batch_no ?? 0),
        reason: r.reason == null ? null : String(r.reason).trim() || null,
        created_by: String(r.created_by ?? '').trim(),
        created_at: String(r.created_at ?? ''),
        exported_by: r.exported_by == null ? null : String(r.exported_by).trim() || null,
        exported_at: r.exported_at == null ? null : String(r.exported_at),
      } satisfies DemOrderBatch;
    })
    .filter((b) => Boolean(b.id))
    .sort((a, b) => a.batch_no - b.batch_no);
}

export async function fetchOrderBatches(orderId: string): Promise<DemOrderBatch[]> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('order_batches')
    .select('id, order_id, batch_no, reason, created_by, created_at, exported_by, exported_at')
    .eq('order_id', orderId)
    .order('batch_no', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));
  return parseOrderBatches(data ?? []);
}

export async function addOrderAmendment(input: {
  order_id: string;
  reason: string;
  created_by: string;
  lines: DemOrderBatchLineInput[];
}): Promise<{ batch: DemOrderBatch }> {
  const orderId = input.order_id.trim();
  const reason = input.reason.trim();
  const createdBy = input.created_by.trim();
  if (!orderId) throw new Error('Order id is required.');
  if (!reason) throw new Error('Add-on reason is required.');
  if (!createdBy) throw new Error('Creator is required.');

  const lines = input.lines
    .map((line) => ({
      item_code: line.item_code.trim().toUpperCase(),
      description: line.description.trim(),
      qty: Number(line.qty),
      uom: line.uom.trim() || 'PCS',
    }))
    .filter((line) => line.item_code && Number.isFinite(line.qty) && line.qty > 0);

  if (lines.length === 0) {
    throw new Error('Add at least one item with quantity.');
  }

  const db = getDbSupabase('dem');
  const { data: orderRow, error: orderError } = await db
    .from('orders')
    .select('order_id, status, customer_code, delivery_date, total_amount')
    .eq('order_id', orderId)
    .maybeSingle();
  if (orderError) throw new Error(formatSupabaseError(orderError));
  if (!orderRow) throw new Error('Order not found.');
  const order = orderRow as {
    status?: string;
    customer_code?: string;
    delivery_date?: string;
    total_amount?: number | string | null;
  };
  const status = String(order.status ?? '');
  if (!canAmendOrder(status)) {
    throw new Error('Add-on is only allowed when the order is Pending or Processed.');
  }

  const customerCode = String(order.customer_code ?? '').trim();
  const deliveryDate = String(order.delivery_date ?? '').trim().split('T')[0] ?? '';
  const currentTotal = Number(order.total_amount ?? 0);
  let amendmentAmount = 0;
  if (customerCode) {
    const prices = await fetchPricesForItems(lines.map((line) => line.item_code));
    const customers: DemCustomer[] = [];
    const customer = await fetchDemCustomer(customerCode);
    if (customer) customers.push(customer);
    const mainCode = demMainCustomerCode(customerCode);
    if (mainCode && mainCode.toUpperCase() !== customerCode.toUpperCase()) {
      const parent = await fetchDemCustomer(mainCode);
      if (parent) customers.push(parent);
    }
    for (const line of lines) {
      const unit = getNavisionPrice(prices, {
        itemCode: line.item_code,
        custCode: customerCode,
        qty: line.qty,
        uom: line.uom,
        deliveryDate,
        customers,
      });
      amendmentAmount += navRound(line.qty * unit);
    }
    amendmentAmount = navRound(amendmentAmount);
  }

  const existing = await fetchOrderBatches(orderId);
  const nextNo = existing.length ? Math.max(...existing.map((b) => b.batch_no)) + 1 : 1;

  const { data: batchRow, error: batchError } = await db
    .from('order_batches')
    .insert({
      order_id: orderId,
      batch_no: nextNo,
      reason,
      created_by: createdBy,
    })
    .select('id, order_id, batch_no, reason, created_by, created_at, exported_by, exported_at')
    .single();
  if (batchError) throw new Error(formatSupabaseError(batchError));

  const batch = parseOrderBatches([batchRow])[0]!;
  const itemsPayload = lines.map((line) => ({
    order_id: orderId,
    batch_id: batch.id,
    item_code: line.item_code,
    description: line.description,
    qty: line.qty,
    uom: line.uom,
  }));

  const { error: itemsError } = await db.from('order_items').insert(itemsPayload);
  if (itemsError) {
    await db.from('order_batches').delete().eq('id', batch.id);
    throw new Error(formatSupabaseError(itemsError));
  }

  const { error: updateError } = await db
    .from('orders')
    .update({
      status: 'Pending',
      created_by: createdBy,
      exported_by: null,
      checked_at: null,
      checked_by: null,
      total_amount: navRound(Math.max(0, currentTotal) + amendmentAmount),
    })
    .eq('order_id', orderId);
  if (updateError) throw new Error(formatSupabaseError(updateError));

  try {
    const { insertOrderChangeLog } = await import('./demOrderEdit');
    await insertOrderChangeLog({
      order_id: orderId,
      changed_by: createdBy,
      change_type: 'amend_batch',
      summary: reason
        ? `Add-on batch #${batch.batch_no}: ${reason}`
        : `Add-on batch #${batch.batch_no}`,
      after_json: { batch_no: batch.batch_no, reason, lines },
    });
  } catch {
    // Audit is best-effort — amendment already saved.
  }

  return { batch };
}

export async function markBatchesExported(
  batchIds: string[],
  exportedBy: string,
): Promise<void> {
  const ids = [...new Set(batchIds.map((id) => id.trim()).filter(Boolean))];
  if (!ids.length) return;
  const db = getDbSupabase('dem');
  const { error } = await db
    .from('order_batches')
    .update({
      exported_by: exportedBy.trim(),
      exported_at: new Date().toISOString(),
    })
    .in('id', ids)
    .is('exported_at', null);
  if (error) throw new Error(formatSupabaseError(error));
}

/** Map item code for NAV DET (delivery fee → NEW PRODUCT). */
export function mapDetItemCode(itemCode: string, description: string): string {
  const code = itemCode.trim();
  const desc = description.trim();
  if (
    code === '401090' ||
    code === '601158' ||
    desc.includes('Delivery Fee') ||
    desc.includes('Delivery Charge')
  ) {
    return 'NEW PRODUCT';
  }
  return code;
}

export function isDeliveryFeeLine(itemCode: string, description: string): boolean {
  const code = itemCode.trim();
  const desc = description.trim();
  return (
    code === 'NEW PRODUCT' ||
    code === '401090' ||
    code === '601158' ||
    desc.includes('401090') ||
    desc.includes('601158') ||
    desc.includes('Delivery Charge') ||
    desc.includes('Delivery Fee')
  );
}
